import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { X, Filter, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { useAppearance } from "../../contexts/AppearanceContext";

export default function ReportFilterBar({hook, extraFilters, children }) {
  const { t } = useTranslation();
  const { filters, updateFilter, updatePeriodPreset, resetFilters, pipelines, responsaveis, PERIOD_PRESETS } = hook;
  const [showAdvanced, setShowAdvanced] = useState(false);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";

  // Count active advanced filters for badge
  const advancedCount = [
    filters.responsavelIds?.length > 0,
    filters.status?.length > 0,
  ].filter(Boolean).length;

  const clearAll = () => {
    resetFilters();
    setShowAdvanced(false);
  };

  const hasAnyFilter = filters.pipelineIds?.length > 0 || filters.responsavelIds?.length > 0 || filters.status?.length > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6">
      {/* Row 1: Label left + Avançado/Limpar right */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("common.filter")}</span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setShowAdvanced(v => !v)}
            className={`flex items-center gap-1.5 text-xs h-8 px-3 rounded-lg border transition-all font-medium ${showAdvanced ? "bg-slate-100 border-slate-300 text-slate-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
            <Filter className="w-3 h-3" />
            {t("common.advanced")}
            {advancedCount > 0 && (
              <span className="w-4 h-4 rounded-full text-white text-[9px] flex items-center justify-center" style={{ backgroundColor: tc500 }}>{advancedCount}</span>
            )}
            {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {hasAnyFilter && (
            <Button variant="ghost" size="sm" onClick={clearAll} className="text-xs text-slate-400 hover:text-rose-500 h-8 gap-1">
              <RotateCcw className="w-3 h-3" />{t("common.clear")}</Button>
          )}
        </div>
      </div>

      {/* Row 2: Filter controls */}
      <div className="flex items-center gap-2 px-4 pb-3 flex-wrap">
        {/* Pipeline Select */}
        <Select
          value={filters.pipelineIds?.length === 1 ? filters.pipelineIds[0] : "all"}
          onValueChange={v => updateFilter("pipelineIds", v === "all" ? [] : [v])}>
          <SelectTrigger className="h-8 text-xs w-40 flex-shrink-0 bg-white">
            <SelectValue placeholder={t("relatorios.allPipelines")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">{t("relatorios.allPipelines")}</SelectItem>
            {pipelines.map(p => (
              <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Period preset */}
        <Select value={filters.periodPreset} onValueChange={updatePeriodPreset}>
          <SelectTrigger className="h-8 text-xs w-36 flex-shrink-0"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIOD_PRESETS.map(p => <SelectItem key={p.value} value={p.value} className="text-xs">{t(p.labelKey)}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Custom date range */}
        {filters.periodPreset === "custom" && (
          <>
            <Input type="date" value={filters.periodStart} onChange={e => updateFilter("periodStart", e.target.value)} className="h-8 text-xs w-36 flex-shrink-0" />
            <span className="text-xs text-slate-400 flex-shrink-0">{t("common.to")}</span>
            <Input type="date" value={filters.periodEnd} onChange={e => updateFilter("periodEnd", e.target.value)} className="h-8 text-xs w-36 flex-shrink-0" />
          </>
        )}

        {/* Period date field */}
        <Select value={filters.periodField} onValueChange={v => updateFilter("periodField", v)}>
          <SelectTrigger className="h-8 text-xs w-36 flex-shrink-0"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at" className="text-xs">{t("ui.data_criacao")}</SelectItem>
            <SelectItem value="ganho_em" className="text-xs">{t("ui.data_ganho")}</SelectItem>
            <SelectItem value="perdido_em" className="text-xs">{t("ui.data_perdido")}</SelectItem>
            <SelectItem value="estagio_updated_at" className="text-xs">{t("ui.ultima_movimentacao")}</SelectItem>
          </SelectContent>
        </Select>

        {extraFilters}
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="border-t border-slate-100 px-4 py-3 flex items-center gap-3 flex-wrap bg-slate-50/50">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex-shrink-0">{t("relatorios.additionalFilters")}</span>

          {/* Responsável */}
          {responsaveis.length > 0 && (
            <Select
              value={filters.responsavelIds?.length === 1 ? filters.responsavelIds[0] : "all"}
              onValueChange={v => updateFilter("responsavelIds", v === "all" ? [] : [v])}>
              <SelectTrigger className="h-8 text-xs w-44 bg-white flex-shrink-0">
                <SelectValue placeholder={t("ui.todos_responsaveis")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">{t("ui.todos_responsaveis")}</SelectItem>
                {responsaveis.map(r => (
                  <SelectItem key={r.user_id} value={r.user_id} className="text-xs">{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Status */}
          <Select
            value={filters.status?.length === 1 ? filters.status[0] : "all"}
            onValueChange={v => updateFilter("status", v === "all" ? [] : [v])}>
            <SelectTrigger className="h-8 text-xs w-40 bg-white flex-shrink-0">
              <SelectValue placeholder={t("ui.todos_status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">{t("ui.todos_status")}</SelectItem>
              <SelectItem value="aberto" className="text-xs">{t("negocios.open")}</SelectItem>
              <SelectItem value="ganho" className="text-xs">{t("ui.ganho")}</SelectItem>
              <SelectItem value="perdido" className="text-xs">{t("ui.perdido")}</SelectItem>
            </SelectContent>
          </Select>

          {(filters.responsavelIds?.length > 0 || filters.status?.length > 0) && (
            <span className="text-xs text-slate-400 ml-1">{t("ui.filtros_aplicados")}</span>
          )}
        </div>
      )}

      {/* Active filter chips — all in theme color */}
      {(filters.pipelineIds?.length > 0 || filters.responsavelIds?.length > 0 || filters.status?.length > 0) && (
        <div className="flex items-center gap-2 px-4 pb-3 flex-wrap border-t border-slate-50 pt-2">
          {filters.pipelineIds?.length > 0 && (
            <Badge className="border-0 gap-1 text-xs font-medium text-white" style={{ backgroundColor: tc500 }}>
              {pipelines.find(p => p.id === filters.pipelineIds[0])?.name || "Pipeline"}
              <button type="button" onClick={() => updateFilter("pipelineIds", [])}><X className="w-3 h-3" /></button>
            </Badge>
          )}
          {filters.responsavelIds?.length > 0 && (
            <Badge className="border-0 gap-1 text-xs font-medium text-white" style={{ backgroundColor: tc500 }}>
              {responsaveis.find(r => r.user_id === filters.responsavelIds[0])?.name || t("ui.responsavel")}
              <button type="button" onClick={() => updateFilter("responsavelIds", [])}><X className="w-3 h-3" /></button>
            </Badge>
          )}
          {filters.status?.length > 0 && (
            <Badge className="border-0 gap-1 text-xs font-medium text-white" style={{ backgroundColor: tc500 }}>
              {filters.status[0] === "ganho" ? t("ui.ganho") : filters.status[0] === "perdido" ? t("ui.perdido") : t("negocios.open")}
              <button type="button" onClick={() => updateFilter("status", [])}><X className="w-3 h-3" /></button>
            </Badge>
          )}
        </div>
      )}

      {children && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}
